import { useCallback, useEffect, useState } from 'react';
import {
  Button, Container, Group, Loader, MultiSelect, NumberInput, ScrollArea,
  Select, Stack, Stepper, Text, TextInput, Title,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useNavigate } from 'react-router-dom';
import { DataTable } from 'mantine-datatable';
import HttpClient from '../helpers/HttpClient';
import { notify } from '../components/NotificationUtils';
import { BookingType } from '../types/BookingModel';
import { Person } from '../types/person.type';
import { Tag } from '../types/tag.type';

const PAGE_SIZE = 20;

export default function BookingBatch() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);

  // Step 1: person selection
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
  const [excludedTags, setExcludedTags] = useState<Tag[]>([]);
  const [page, setPage] = useState(1);
  const [records, setRecords] = useState<Person[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loadingPersons, setLoadingPersons] = useState(true);
  const [selectedPersons, setSelectedPersons] = useState<Person[]>([]);

  // Step 2: booking details
  const [bookingTypes, setBookingTypes] = useState<{ value: string; label: string }[]>([]);
  const [bookingDate, setBookingDate] = useState<Date>(new Date());
  const [bookingTypeId, setBookingTypeId] = useState<string | null>(null);
  const [bookingText, setBookingText] = useState('');
  const [bookingAmount, setBookingAmount] = useState<number | string>(0);
  const [saving, setSaving] = useState(false);

  const fetchPersons = useCallback(async (p: number, tagIds: number[], excludeTagIds: number[]) => {
    setLoadingPersons(true);
    try {
      const params: Record<string, string> = {
        skip: String((p - 1) * PAGE_SIZE),
        take: String(PAGE_SIZE),
        slim: 'true',
      };
      if (tagIds.length) params.tagIds = tagIds.join(',');
      if (excludeTagIds.length) params.excludeTagIds = excludeTagIds.join(',');

      const result = await new HttpClient<Person>('/person').getAllPaged(params);
      setRecords(result.data ?? []);
      setTotalRecords(result.total);
    } catch (error) {
      console.error('API call failed:', error);
    } finally {
      setLoadingPersons(false);
    }
  }, []);

  useEffect(() => {
    new HttpClient<Tag>('/tag').getAll().then(tags => setAllTags([...tags].sort((a, b) => a.name.localeCompare(b.name))));
    new HttpClient<BookingType>('/bookingType').getAll().then(types =>
      setBookingTypes(types.map((t: any) => ({ value: t.id.toString(), label: t.title }))),
    );
  }, []);

  useEffect(() => {
    fetchPersons(page, selectedTags.map(t => t.id), excludedTags.map(t => t.id));
  }, [page, selectedTags, excludedTags, fetchPersons]);

  const handleTagChange = (values: string[]) => {
    setSelectedTags(allTags.filter(tag => values.includes(tag.name)));
    setPage(1);
  };

  const handleExcludeTagChange = (values: string[]) => {
    setExcludedTags(allTags.filter(tag => values.includes(tag.name)));
    setPage(1);
  };

  const handleSubmit = async () => {
    if (!bookingTypeId || !bookingText.trim() || selectedPersons.length === 0) {
      notify('Fehler', 'Bitte alle Pflichtfelder ausfüllen', 'red');
      return;
    }
    setSaving(true);
    try {
      const payload = selectedPersons.map(person => ({
        bookingDate,
        bookingText,
        bookingAmount: Number(bookingAmount),
        person: { id: person.id },
        bookingType: { id: bookingTypeId },
      }));
      const res: any = await new HttpClient<any>('/booking/batch').post(payload as any);
      const results: { status: number }[] = res?.data ?? [];
      const failedCount = results.filter(r => r.status !== 200 && r.status !== 201).length;
      if (results.length !== payload.length || failedCount > 0) {
        notify('Fehler', `${failedCount || payload.length} von ${payload.length} Buchung(en) fehlgeschlagen`, 'red');
        return;
      }
      notify('Erfolg', `${selectedPersons.length} Buchung(en) erstellt`, 'green');
      navigate('/booking');
    } catch {
      notify('Fehler', 'Speichern fehlgeschlagen', 'red');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container fluid p="md">
      <Title order={2} mb="sm">Stapelbuchung</Title>

      <Stepper active={step} allowNextStepsSelect={false}>
        {/* ── Step 1: person selection ── */}
        <Stepper.Step label="Personen" description="Nach Tags filtern und auswählen">
          <Stack mt="md" gap="sm">
            <MultiSelect
              label="Tags einschließen"
              data={allTags.map(tag => tag.name)}
              value={selectedTags.map(tag => tag.name)}
              placeholder="Tags wählen"
              onChange={handleTagChange}
              clearable
            />
            <MultiSelect
              label="Tags ausschließen"
              data={allTags.map(tag => tag.name)}
              value={excludedTags.map(tag => tag.name)}
              placeholder="Tags wählen"
              onChange={handleExcludeTagChange}
              clearable
            />
            <Text size="sm" c="dimmed">{selectedPersons.length} Person(en) ausgewählt</Text>
            {loadingPersons && records.length === 0 ? (
              <Loader size="md" />
            ) : (
              <ScrollArea>
                <DataTable
                  withTableBorder
                  borderRadius="sm"
                  withColumnBorders
                  striped
                  highlightOnHover
                  fetching={loadingPersons}
                  records={records}
                  selectedRecords={selectedPersons}
                  onSelectedRecordsChange={setSelectedPersons}
                  columns={[
                    { accessor: 'lastName', title: 'Nachname' },
                    { accessor: 'firstName', title: 'Vorname' },
                    {
                      accessor: 'tags',
                      title: 'Tags',
                      render: ({ tags }) => Array.isArray(tags) ? tags.map(t => t.name).join(', ') : '',
                    },
                  ]}
                  totalRecords={totalRecords}
                  recordsPerPage={PAGE_SIZE}
                  page={page}
                  onPageChange={setPage}
                />
              </ScrollArea>
            )}
            <Group mt="xs">
              <Button onClick={() => setStep(1)} disabled={selectedPersons.length === 0}>
                Weiter
              </Button>
              <Button variant="subtle" onClick={() => navigate('/booking')}>
                Abbrechen
              </Button>
            </Group>
          </Stack>
        </Stepper.Step>

        {/* ── Step 2: booking details ── */}
        <Stepper.Step label="Buchung" description="Details eingeben">
          <Stack mt="md" gap="sm" maw={560}>
            <Text size="sm" c="dimmed">
              Wird auf {selectedPersons.length} Person(en) angewendet
            </Text>
            <DatePickerInput
              label="Datum"
              value={bookingDate}
              onChange={(v: any) => setBookingDate(v ? new Date(v) : new Date())}
              valueFormat="DD.MM.YYYY"
              required
            />
            <Select
              label="Buchungsart"
              data={bookingTypes}
              value={bookingTypeId}
              onChange={setBookingTypeId}
              required
            />
            <TextInput
              label="Text"
              value={bookingText}
              onChange={(e) => { const v = e.currentTarget.value; setBookingText(v); }}
              required
            />
            <NumberInput
              label="Betrag (€)"
              value={bookingAmount}
              onChange={setBookingAmount}
              decimalSeparator=","
              prefix="€ "
            />
            <Group mt="xs">
              <Button onClick={handleSubmit} loading={saving}>
                Buchungen erstellen ({selectedPersons.length})
              </Button>
              <Button variant="subtle" onClick={() => setStep(0)}>
                Zurück
              </Button>
              <Button variant="subtle" onClick={() => navigate('/booking')}>
                Abbrechen
              </Button>
            </Group>
          </Stack>
        </Stepper.Step>
      </Stepper>
    </Container>
  );
}
